/* build: 2026-09-25b — title size rebuilt from custom.title_ft + custom.title_mm in every language, exact inches in the size table */
/**
 * DPlans Measurement System
 * --------------------------------------------------------------
 * Persistent imperial / metric / both preference across pages
 * and language switches.
 *
 * Storage keys (window.localStorage):
 *   dplans_unit_preference   'both' | 'metric' | 'imperial'
 *   dplans_last_locale       last seen Shopify locale (e.g. 'en', 'de')
 *   dplans_unit_popup_shown  'true' once the disclaimer popup was answered
 *
 * Body data-attribute drives the CSS:
 *   <body data-unit-preference="both|metric|imperial">
 *
 * Auto-conversion of structured product specs:
 *   <span data-spec-mm="3645" data-spec-in="144" data-spec-unit="mm">3645 mm</span>
 *   (data-spec-in = exact inches from the plans; without it inches are computed from mm)
 *   <span data-spec-area="26.5 m²">26.5 m²</span>
 * Inline markup of free-form unit text:
 *   <span data-unit="metric">...</span><span data-unit="imperial">...</span>
 */
(function () {
  'use strict';

  var STORAGE = {
    pref: 'dplans_unit_preference',
    locale: 'dplans_last_locale',
    popup: 'dplans_unit_popup_shown'
  };

  var VALID = ['both', 'metric', 'imperial'];
  var DEFAULT_PREF = 'both';

  function safeGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function safeSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* ignore */ }
  }

  function getPreference() {
    var stored = safeGet(STORAGE.pref);
    return VALID.indexOf(stored) > -1 ? stored : DEFAULT_PREF;
  }

  function setPreference(value, opts) {
    if (VALID.indexOf(value) === -1) return;
    safeSet(STORAGE.pref, value);
    applyPreference(value);
    document.dispatchEvent(new CustomEvent('dplans:unit-changed', {
      detail: { preference: value, source: (opts && opts.source) || 'api' }
    }));
  }

  function applyPreference(value) {
    document.body.setAttribute('data-unit-preference', value);
    var buttons = document.querySelectorAll('[data-measurement-toggle]');
    for (var i = 0; i < buttons.length; i++) {
      buttons[i].setAttribute('data-current', value);
      var options = buttons[i].querySelectorAll('[data-unit-value]');
      for (var j = 0; j < options.length; j++) {
        var opt = options[j];
        var isActive = opt.getAttribute('data-unit-value') === value;
        opt.classList.toggle('is-active', isActive);
        opt.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      }
    }
    renderConvertibleSpecs(value);
  }

  /* ---------------- Unit conversion ---------------- */

  function mmToFtIn(mm) {
    if (!isFinite(mm)) return '';
    var totalIn = Math.round(mm / 25.4);
    var feet = Math.floor(totalIn / 12);
    var inches = totalIn - feet * 12;
    if (feet === 0) return inches + ' in';
    if (inches === 0) return feet + ' ft';
    return feet + ' ft ' + inches + ' in';
  }

  /* Exact inches (from the plans) -> "11 ft 11 in" */
  function inToFtIn(inches) {
    if (!isFinite(inches)) return '';
    var totalIn = Math.round(inches);
    var feet = Math.floor(totalIn / 12);
    var rest = totalIn - feet * 12;
    if (feet === 0) return rest + ' in';
    if (rest === 0) return feet + ' ft';
    return feet + ' ft ' + rest + ' in';
  }

  function m2ToFt2(m2) {
    if (!isFinite(m2)) return '';
    return Math.round(m2 * 10.7639) + ' sq ft';
  }

  function formatMm(mm, unitLabel) {
    var label = unitLabel || 'mm';
    return mm + ' ' + label;
  }

  /* Convert "<num> m²" or "<num>m2" or "26.5 m²" → number m² */
  function parseAreaToM2(text) {
    if (!text) return NaN;
    var match = String(text).replace(',', '.').match(/([-+]?\d*\.?\d+)/);
    if (!match) return NaN;
    return parseFloat(match[1]);
  }

  /* Strip any baked-in secondary unit from a source value so the toggle alone
     controls the dual display. Removes a trailing parenthetical conversion
     ("22 m² (237 sq ft)" → "22 m²") and anything after a "/" separator
     ("22 m² / 237 sq ft" → "22 m²"). */
  function metricOnly(text) {
    return String(text).replace(/\s*[\(/].*$/, '').trim();
  }

  function renderConvertibleSpecs(pref) {
    var mmNodes = document.querySelectorAll('[data-spec-mm]');
    for (var i = 0; i < mmNodes.length; i++) {
      var el = mmNodes[i];
      var mm = parseFloat(el.getAttribute('data-spec-mm'));
      var unit = el.getAttribute('data-spec-unit') || 'mm';
      if (!isFinite(mm)) continue;
      var metric = formatMm(mm, unit);
      var exactIn = parseFloat(el.getAttribute('data-spec-in'));
      var imperial = isFinite(exactIn) ? inToFtIn(exactIn) : mmToFtIn(mm);
      if (pref === 'imperial') {
        el.textContent = imperial;
      } else if (pref === 'metric') {
        el.textContent = metric;
      } else {
        el.textContent = metric + ' / ' + imperial;
      }
    }

    var areaNodes = document.querySelectorAll('[data-spec-area]');
    for (var k = 0; k < areaNodes.length; k++) {
      var ae = areaNodes[k];
      var original = ae.getAttribute('data-spec-area');
      var m2 = parseAreaToM2(original);
      if (!isFinite(m2)) continue;
      var metricArea = metricOnly(original);
      var imp = m2ToFt2(m2);
      if (pref === 'imperial') {
        ae.textContent = imp;
      } else if (pref === 'metric') {
        ae.textContent = metricArea;
      } else {
        ae.textContent = metricArea + ' / ' + imp;
      }
    }

    convertTitles(pref);
  }

  /* ---------------- Product title auto-conversion ----------------
     Detects feet/inch dimensions inside product titles and converts
     them to metric. Examples it understands:
        13' 1" x 19' 8"   →  4 m × 6 m
        12'               →  3.7 m
        24"               →  61 cm
     Originals are preserved in data-orig-title and restored when the
     user switches back to imperial.                                  */
  var TITLE_SELECTORS = '.product-card__name, .product-single__title';
  /* Matches dimension prefixes like "12' X 20'", "12' 6\" x 20' 3\"", "12'X20'" */
  var TITLE_DIM_RE = /(\d+\s*['′](?:\s*\d+(?:\.\d+)?\s*["″])?)\s*[xX×]\s*(\d+\s*['′](?:\s*\d+(?:\.\d+)?\s*["″])?)/;
  /* Loose dimension scan for fallback (no metafield data) */
  var LOOSE_DIM_RE = /(\d+)\s*['′]\s*(?:(\d+(?:\.\d+)?)\s*["″])?|(\d+(?:\.\d+)?)\s*["″]/g;

  function metersFromFtIn(ft, inches) {
    return ((ft || 0) * 12 + (inches || 0)) * 0.0254;
  }

  function formatMetersLoose(m) {
    if (m < 1) return Math.round(m * 100) + ' cm';
    return (Math.round(m * 10) / 10) + ' m';
  }

  function convertTitleLoose(original, pref) {
    if (pref === 'imperial') return original;
    return original.replace(LOOSE_DIM_RE, function (match, ft, inches, lonelyInches) {
      var meters;
      if (ft !== undefined) {
        meters = metersFromFtIn(parseInt(ft, 10), inches ? parseFloat(inches) : 0);
      } else if (lonelyInches !== undefined) {
        meters = metersFromFtIn(0, parseFloat(lonelyInches));
      } else {
        return match;
      }
      var metric = formatMetersLoose(meters);
      if (pref === 'both') return match + ' (' + metric + ')';
      return metric;
    });
  }

  /* Authoritative path: metafield mm values drive the rewrite.
     Replaces the dimension prefix (e.g. "12' X 20'") with an
     accurately-converted version derived from the source mm. */
  function rewriteTitleFromMm(original, widthMm, lengthMm, pref) {
    if (!TITLE_DIM_RE.test(original)) {
      return original;
    }
    var imperial = mmToFtIn(widthMm) + ' × ' + mmToFtIn(lengthMm);
    var metric = Math.round(widthMm) + ' × ' + Math.round(lengthMm) + ' mm';
    if (pref === 'imperial') {
      return original.replace(TITLE_DIM_RE, imperial);
    }
    if (pref === 'metric') {
      return original.replace(TITLE_DIM_RE, metric);
    }
    /* both — keep the original prefix, append metric after it */
    return original.replace(TITLE_DIM_RE, function (m) {
      return m + ' (' + metric + ')';
    });
  }

  /* ---- Title size from the custom.title_mm metafield (2026-09-25) ----
     Stored titles start with the size in feet, optionally already followed by
     the metric in brackets (translations and metric-market English):
       "12' x 20' Movable Timber-Frame Cabin Plans | ..."
       "12' x 20' (3640 × 5945 mm) Tegninger til ..."
       "23' x 20' + 26' x 7' Small House ..."            (two-part building)
     custom.title_mm holds the metric in the TITLE's order, all parts:
       "3640 × 5945" / "6925 × 6005 + 7765 × 2272" / "7420 × 7420 | 7420 × 3000"
       ("|" = a second size later in the title, e.g. "... Garage & 24' x 10' Carport")
     Output per preference:
       both      12' x 20' (3640 × 5945 mm) Name
       metric    3640 × 5945 mm Name
       imperial  12' x 20' Name          (the title's own feet, never recalculated) */
  var FT_SRC = "\\d+\\s*['′](?:\\s*\\d+(?:\\.\\d+)?\\s*[\"″])?";
  var PAIR_SRC = FT_SRC + '\\s*[xX×]\\s*' + FT_SRC;
  var MM_PAREN_SRC = '(\\s*\\([^()]*?\\bmm\\))?';
  var LEAD_RE = new RegExp('^\\s*(' + PAIR_SRC + '(?:\\s*\\+\\s*' + PAIR_SRC + ')*)' + MM_PAREN_SRC);
  var LATER_RE = new RegExp('(' + PAIR_SRC + ')' + MM_PAREN_SRC, 'g');

  function trimStr(s) { return String(s || '').replace(/^\s+|\s+$/g, ''); }

  function mmFromParen(p) {
    return p ? trimStr(p).replace(/^\(\s*/, '').replace(/\s*mm\)$/, '') : '';
  }

  function sizeText(imperial, mm, pref) {
    if (!mm || pref === 'imperial') return imperial;
    if (pref === 'metric') return mm + ' mm';
    return imperial + ' (' + mm + ' mm)';
  }

  function ftInToInches(s) {
    var m = String(s).match(/(\d+)\s*['′](?:\s*(\d+(?:\.\d+)?)\s*["″])?/);
    return m ? parseInt(m[1], 10) * 12 + (m[2] ? parseFloat(m[2]) : 0) : NaN;
  }

  /* Fallback when a product has no title_mm yet: width/length in the title's order */
  function mmInTitleOrder(pairText, widthMm, lengthMm) {
    var sides = pairText.split(/\s*[xX×]\s*/);
    var a = ftInToInches(sides[0]), b = ftInToInches(sides[1]);
    var wi = widthMm / 25.4, li = lengthMm / 25.4;
    var same = Math.abs(a - wi) + Math.abs(b - li);
    var swap = Math.abs(a - li) + Math.abs(b - wi);
    var w = Math.round(widthMm), l = Math.round(lengthMm);
    return swap < same ? l + ' × ' + w : w + ' × ' + l;
  }

  /* ---- Rebuild from custom.title_ft + custom.title_mm (any stored format) ----
     title_ft: the English title's feet size ("12' x 20'", "23' x 20' + 26' x 7'",
     "24' x 24' | 24' x 10'"); title_mm: the same sizes in mm. The size is removed
     from the stored title wherever the translation put it (feet first, metric first
     "3,64 x 5,95 m ...", or Spanish "... de 12' x 20' (3.64 x 5.95 m) ...") and the
     chosen unit is put back at the front. */
  var NUM_SRC = '\\d+(?:[.,]\\d+)?';
  var MET_SRC = NUM_SRC + '\\s?[x×]\\s?' + NUM_SRC + '\\s?m\\b';
  var MET_LEAD_RE = new RegExp('^\\s*' + MET_SRC + '(?:\\s*\\+\\s*' + MET_SRC + ')*');
  var IMP_MET_SRC = PAIR_SRC + '\\s*\\(\\s*' + MET_SRC + '\\s*\\)';
  var ES_MID_RE = new RegExp('\\s*(?:\\bde\\s+)?' + IMP_MET_SRC + '(?:\\s*\\+\\s*' + IMP_MET_SRC + ')*');
  var LATER_ANY_RE = new RegExp(PAIR_SRC + '(?:\\s*\\([^()]*?\\b(?:mm|m)\\))?|' + MET_SRC, 'g');

  function splitSegs(v) {
    var t = trimStr(v);
    if (!t) return [];
    var parts = t.split('|');
    for (var i = 0; i < parts.length; i++) parts[i] = trimStr(parts[i]);
    return parts;
  }

  function tidyName(s) {
    return String(s).replace(/\s{2,}/g, ' ').replace(/\s+\|/g, ' |').replace(/\|\s*$/, '').replace(/^\s+|\s+$/g, '');
  }

  function rebuildTitle(original, titleFt, titleMm, pref) {
    var ft = splitSegs(titleFt), mm = splitSegs(titleMm);
    if (!ft.length || ft.length !== mm.length) return null;
    var name, m;
    if ((m = original.match(LEAD_RE))) {
      name = original.slice(m[0].length);
    } else if ((m = original.match(MET_LEAD_RE))) {
      name = original.slice(m[0].length);
    } else if ((m = ES_MID_RE.exec(original))) {
      name = original.slice(0, m.index) + ' ' + original.slice(m.index + m[0].length);
    } else {
      return null;
    }
    var k = 0;
    name = name.replace(LATER_ANY_RE, function (match) {
      k += 1;
      return k < ft.length ? sizeText(ft[k], mm[k], pref) : match;
    });
    return sizeText(ft[0], mm[0], pref) + ' ' + tidyName(name);
  }

  /* Returns the rewritten title, or null when the title does not start with a size. */
  function rewriteTitle(original, titleMm, widthMm, lengthMm, pref) {
    var lead = original.match(LEAD_RE);
    if (!lead) return null;
    var segs = trimStr(titleMm) ? String(titleMm).split('|') : [];
    for (var s = 0; s < segs.length; s++) segs[s] = trimStr(segs[s]);
    var leadImperial = trimStr(lead[1]);
    var leadMm = segs[0] || mmFromParen(lead[2]);
    if (!leadMm && leadImperial.indexOf('+') === -1 && isFinite(widthMm) && isFinite(lengthMm)) {
      leadMm = mmInTitleOrder(leadImperial, widthMm, lengthMm);
    }
    var k = 0;
    var rest = original.slice(lead[0].length).replace(LATER_RE, function (m, imp, paren) {
      k += 1;
      return sizeText(trimStr(imp), segs[k] || mmFromParen(paren), pref);
    });
    return sizeText(leadImperial, leadMm, pref) + rest;
  }

  /* which = 'mm' or 'ft': reads data-title-<which> on the title, the card marker
     (data-card-title-<which>, product-card-specs snippet) or the product page marker
     (data-page-title-<which>, measurement-popup snippet). */
  function titleSizeFor(el, which) {
    var own = el.getAttribute('data-title-' + which);
    if (own !== null) return own;
    var info = el.closest ? el.closest('.product-card__info') : null;
    var marker = info ? info.querySelector('[data-card-title-' + which + ']') : null;
    if (marker) return marker.getAttribute('data-card-title-' + which);
    if (el.classList && el.classList.contains('product-single__title')) {
      var page = document.querySelector('[data-page-title-' + which + ']');
      if (page) return page.getAttribute('data-page-title-' + which);
    }
    return '';
  }

  function convertTitles(pref) {
    var nodes = document.querySelectorAll(TITLE_SELECTORS);
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var original = el.getAttribute('data-orig-title');
      if (original === null) {
        original = el.textContent.trim().replace(/\s+/g, ' ');
        el.setAttribute('data-orig-title', original);
      }
      var mmWidth = parseFloat(el.getAttribute('data-mm-width'));
      var mmLength = parseFloat(el.getAttribute('data-mm-length'));
      var titleMm = titleSizeFor(el, 'mm');
      var out = rebuildTitle(original, titleSizeFor(el, 'ft'), titleMm, pref);
      if (out === null) out = rewriteTitle(original, titleMm, mmWidth, mmLength, pref);
      if (out === null) {
        out = (isFinite(mmWidth) && isFinite(mmLength))
          ? rewriteTitleFromMm(original, mmWidth, mmLength, pref)
          : convertTitleLoose(original, pref);
      }
      if (el.textContent !== out) el.textContent = out;
    }
  }

  /* ---------------- Toggle wiring ---------------- */

  function cycle(current) {
    var idx = VALID.indexOf(current);
    return VALID[(idx + 1) % VALID.length];
  }

  function handleToggleClick(event) {
    var target = event.target.closest('[data-unit-value]');
    var container = event.target.closest('[data-measurement-toggle]');
    if (!container) return;

    if (target && target.hasAttribute('data-unit-value')) {
      event.preventDefault();
      setPreference(target.getAttribute('data-unit-value'), { source: 'toggle' });
      return;
    }

    var cycleBtn = event.target.closest('[data-unit-cycle]');
    if (cycleBtn) {
      event.preventDefault();
      setPreference(cycle(getPreference()), { source: 'toggle-cycle' });
    }
  }

  function bindToggles() {
    document.addEventListener('click', handleToggleClick);
  }

  /* ---------------- Popup on language switch ---------------- */

  function maybeShowPopup(currentLocale) {
    var popup = document.getElementById('MeasurementPopup');
    if (!popup) return;

    // English audience defaults to imperial — never ask.
    var isEnglish = currentLocale && currentLocale.toLowerCase().indexOf('en') === 0;
    if (isEnglish) return;

    // Ask on every actual language change (not on every page load within the
    // same language): show only when the current locale differs from the last
    // one we recorded. init() stores STORAGE.locale *after* this runs, so here
    // it still holds the previously seen locale.
    var lastLocale = safeGet(STORAGE.locale);
    if (lastLocale === currentLocale) return;

    popup.removeAttribute('hidden');
    popup.classList.add('is-open');
    document.body.classList.add('measurement-popup-open');

    var choices = popup.querySelectorAll('[data-popup-choice]');
    for (var i = 0; i < choices.length; i++) {
      choices[i].addEventListener('click', function (e) {
        var value = e.currentTarget.getAttribute('data-popup-choice');
        if (VALID.indexOf(value) > -1) {
          setPreference(value, { source: 'popup' });
        }
        closePopup(popup);
      });
    }

    var dismissBtns = popup.querySelectorAll('[data-popup-dismiss]');
    for (var k = 0; k < dismissBtns.length; k++) {
      dismissBtns[k].addEventListener('click', function () { closePopup(popup); });
    }
  }

  function closePopup(popup) {
    popup.classList.remove('is-open');
    popup.setAttribute('hidden', 'hidden');
    document.body.classList.remove('measurement-popup-open');
    safeSet(STORAGE.popup, 'true');
  }

  /* ---------------- Dynamic grid (AJAX filtering / pagination) ----------------
     Facet filtering and "load more" replace #ProductGridContainer via innerHTML
     without firing shopify:section:load, so freshly injected product cards keep
     their raw markup. Re-apply the preference whenever the grid mutates.
     The observer is disconnected while we rewrite spec text (which itself mutates
     the DOM) to avoid an infinite re-apply loop. */
  var gridObserver = null;

  function observeDynamicGrid() {
    var container = document.getElementById('ProductGridContainer');
    if (!container || typeof MutationObserver === 'undefined') return;

    var scheduled = false;
    gridObserver = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.setTimeout(function () {
        scheduled = false;
        gridObserver.disconnect();
        applyPreference(getPreference());
        gridObserver.observe(container, { childList: true, subtree: true });
      }, 50);
    });
    gridObserver.observe(container, { childList: true, subtree: true });
  }

  /* ---------------- Init ---------------- */

  function init() {
    var locale = (document.documentElement.getAttribute('lang') || 'en').toLowerCase();

    applyPreference(getPreference());
    bindToggles();
    maybeShowPopup(locale);
    safeSet(STORAGE.locale, locale);
    observeDynamicGrid();

    document.addEventListener('shopify:section:load', function () {
      applyPreference(getPreference());
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.DPlansMeasurement = {
    get: getPreference,
    set: setPreference,
    KEYS: STORAGE,
    rewriteTitle: rewriteTitle,
    rebuildTitle: rebuildTitle
  };
})();
